import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/onboarding :: CdlInterestStep
 *
 * Step 5. Single boolean toggle — "am I interested in CDL content?". Always
 * valid; default false. The wizard's review step shows the final choice
 * before PUT /api/profile.
 */
export interface CdlInterestStepProps {
  value: boolean;
  onNext: (cdlInterest: boolean) => void;
  onBack: () => void;
}

export default function CdlInterestStep({
  value,
  onNext,
  onBack,
}: CdlInterestStepProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<boolean>(value);

  return (
    <div className="space-y-md">
      <header>
        <h2 className="font-display text-xl font-semibold text-brand-primary">
          {t("onboarding.cdlInterest.title")}
        </h2>
        <p className="mt-xs text-sm text-neutral-700">
          {t("onboarding.cdlInterest.body")}
        </p>
      </header>

      <section className="rounded-md border border-brand-primary/10 bg-white p-md">
        <div className="flex items-start justify-between gap-md">
          <div className="flex-1">
            <label
              htmlFor="cdl-interest-toggle"
              className="text-sm font-semibold text-brand-primary"
            >
              {t("onboarding.cdlInterest.toggleLabel")}
            </label>
            <p className="mt-xs text-xs text-neutral-600">
              {draft
                ? t("onboarding.cdlInterest.hintOn")
                : t("onboarding.cdlInterest.hintOff")}
            </p>
          </div>
          <div className="shrink-0">
            <label className="relative inline-flex cursor-pointer items-center">
              <input
                id="cdl-interest-toggle"
                type="checkbox"
                role="switch"
                checked={draft}
                onChange={(ev) => setDraft(ev.target.checked)}
                data-testid="cdl-interest-toggle"
                className="peer sr-only"
              />
              <span
                aria-hidden="true"
                className="h-6 w-11 rounded-full bg-neutral-300 transition-colors after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5"
              />
            </label>
          </div>
        </div>
      </section>

      <div className="flex items-center justify-between gap-sm pt-md">
        <button
          type="button"
          data-testid="onboarding-back"
          onClick={onBack}
          className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-md py-sm text-sm font-medium text-brand-primary transition-colors hover:border-brand-primary/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.back")}
        </button>
        <button
          type="button"
          data-testid="onboarding-next"
          onClick={() => onNext(draft)}
          className="inline-flex items-center justify-center rounded-md bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("onboarding.next")}
        </button>
      </div>
    </div>
  );
}
